import React, { useEffect, useState, useCallback } from "react";
import { Route, Routes, useParams } from "react-router-dom";
import { useStateMachine } from "little-state-machine";
import { useIntl } from "react-intl";
import Cookies from "js-cookie";

import Wrapper from "components/organisms/Wrapper/Wrapper";
import { updateShipmentId } from "store/store";
import ShipmentRouteRedirection from "components/organisms/ShipmentRouteRedirection/ShipmentRouteRedirection";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { ShipmentTrackingContextProvider } from "pages/common/ShipmentTracking/ShipmentTrackingContext";
import ShipmentTrackingV2 from "pages/common/ShipmentTrackingV2/ShipmentTrackingV2";

// eslint-disable-next-line import/no-cycle
import {
  ShipmentQuotes,
  ShipmentDetails,
  QuickTracking,
  ShipmentBilling,
  ShipmentAuditTrail,
  ShipmentQuotesV2,
} from "../..";

function ShipmentDashboard() {
  const intl = useIntl();
  const { state, actions } = useStateMachine({ updateShipmentId });
  const { id: shipmentIdUrl } = useParams();
  const { shipmentId } = state;
  const isQuickRequest = document.location.pathname.includes("quick");
  const isGuest = Cookies.get("is_guest") === "true";
  const [isConsolidated, setIsConsolidated] = useState(false);

  const { data: shipmentData, isInitialLoading: isLoading } = useGetShipmentBasic(shipmentIdUrl, {
    enabled: !isQuickRequest && !!shipmentIdUrl && /(S|QR)\d+/g.test(shipmentIdUrl),
    staleTime: 45 * 1000, // 45 seconds
  });
  const isOffPlatform = shipmentData?.shipment?.is_off_platform_shipment;

  useEffect(() => {
    if (["S", "Q"].includes(shipmentIdUrl.charAt(0)) && (!shipmentId || shipmentId !== shipmentIdUrl)) {
      actions.updateShipmentId({
        shipmentId: shipmentIdUrl,
      });
    }
  }, [shipmentIdUrl]);

  const handleSetIsConsolidated = useCallback((value) => {
    setIsConsolidated(value);
  }, []);

  return (
    <Routes>
      <Route
        path="/detail"
        element={
          <Wrapper
            pagekey={`/shipment/${shipmentIdUrl}/detail`}
            tabTitle={intl.formatMessage({
              id: "quote.quoteDetailsTabTitle",
              defaultMessage: "Quote Details",
            })}
            isConsolidated={isConsolidated}
            shipmentIdOnTab
            component={<ShipmentDetails />}
            isLoading={isLoading}
          />
        }
      />
      <Route
        path="/tracking"
        element={
          <ShipmentTrackingContextProvider>
            <Wrapper
              pagekey={`/shipment/${shipmentIdUrl}/tracking`}
              tabTitle={intl.formatMessage({
                id: "general.tracking",
                defaultMessage: "Tracking",
              })}
              shipmentIdOnTab
              component={<ShipmentTrackingV2 isOffPlatform={isOffPlatform} />}
              isLoading={isLoading}
            />
          </ShipmentTrackingContextProvider>
        }
      />
      {/* Guest users do not have access to the following routes */}
      {!isGuest && (
        <>
          <Route
            path="/quick/detail"
            element={
              <Wrapper
                pagekey={`/shipment/${shipmentIdUrl}/quick/detail`}
                tabTitle={intl.formatMessage({
                  id: "quote.quickDetailsTabTitle",
                  defaultMessage: "Quick Details",
                })}
                shipmentIdOnTab
                component={<ShipmentDetails isQuickRequest />}
                isQuickRequest
                isLoading={isLoading}
              />
            }
          />
          <Route
            path="/quick/tracking"
            element={
              <ShipmentTrackingContextProvider>
                <Wrapper
                  pagekey={`/shipment/${shipmentIdUrl}/quick/tracking`}
                  tabTitle={intl.formatMessage({
                    id: "general.tracking",
                    defaultMessage: "Tracking",
                  })}
                  shipmentIdOnTab
                  component={<QuickTracking />}
                  isQuickRequest
                  isLoading={isLoading}
                />
              </ShipmentTrackingContextProvider>
            }
          />
          <Route
            path="/billing"
            element={
              <Wrapper
                pagekey={`/shipment/${shipmentIdUrl}/billing`}
                tabTitle={intl.formatMessage({
                  id: "quote.billingsTabTitle",
                  defaultMessage: "Billing",
                })}
                shipmentIdOnTab
                component={<ShipmentBilling />}
                isLoading={isLoading}
              />
            }
          />
          <Route
            path="/audit-trail"
            element={
              <Wrapper
                pagekey={`/shipment/${shipmentIdUrl}/audit-trail`}
                tabTitle={intl.formatMessage({
                  id: "audit-trail.auditTrail",
                  defaultMessage: "Audit Trail",
                })}
                shipmentIdOnTab
                isConsolidated={isConsolidated}
                component={<ShipmentAuditTrail shipmentId={shipmentIdUrl} />}
                isLoading={isLoading}
              />
            }
          />
          <Route
            path="/quick/audit-trail"
            element={
              <Wrapper
                pagekey={`/shipment/${shipmentIdUrl}/quick/audit-trail`}
                tabTitle={intl.formatMessage({
                  id: "audit-trail.auditTrail",
                  defaultMessage: "Audit Trail",
                })}
                shipmentIdOnTab
                isQuickRequest
                component={<ShipmentAuditTrail shipmentId={shipmentIdUrl} />}
                isLoading={isLoading}
              />
            }
          />
          <Route
            path=""
            element={
              <Wrapper
                pagekey={`/shipment/${shipmentIdUrl}`}
                tabTitle={intl.formatMessage({
                  id: "general.quoteRequests",
                  defaultMessage: "Quote Requests",
                })}
                shipmentIdOnTab
                isConsolidated={isConsolidated}
                component={<ShipmentQuotes setIsConsolidated={handleSetIsConsolidated} />}
                isLoading={isLoading}
              />
            }
            // Quotes V2 route
          />
          <Route
            path="v2"
            element={
              <Wrapper
                pagekey={`/shipment/${shipmentIdUrl}/v2`}
                tabTitle={intl.formatMessage({
                  id: "general.quoteRequests",
                  defaultMessage: "Quote Requests",
                })}
                shipmentIdOnTab
                isConsolidated={isConsolidated}
                component={<ShipmentQuotesV2 setIsConsolidated={handleSetIsConsolidated} />}
                isLoading={isLoading}
              />
            }
          />
          <Route
            exact
            path="/quick"
            element={
              <Wrapper
                pagekey={`/shipment/${shipmentIdUrl}/quick`}
                tabTitle={intl.formatMessage({
                  id: "general.quoteRequests",
                  defaultMessage: "Quote Requests",
                })}
                shipmentIdOnTab
                component={<ShipmentQuotes isQuickRequest />}
                isQuickRequest
                isLoading={isLoading}
              />
            }
          />
          <Route
            exact
            path="/quick/v2"
            element={
              <Wrapper
                pagekey={`/shipment/${shipmentIdUrl}/quick/v2`}
                tabTitle={intl.formatMessage({
                  id: "general.quoteRequests",
                  defaultMessage: "Quote Requests",
                })}
                shipmentIdOnTab
                component={<ShipmentQuotesV2 isQuickRequest />}
                isQuickRequest
                isLoading={isLoading}
              />
            }
          />
        </>
      )}

      {/* Always keep this last item in the file.. */}
      <Route path="*" element={<ShipmentRouteRedirection />} />
    </Routes>
  );
}

export default ShipmentDashboard;
